import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { solicitudesAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CheckCircle2, XCircle, Clock, FileSearch, Shield, Loader2, Download } from 'lucide-react';

const config = {
    creada: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-200' },
    en_verificacion: { icon: FileSearch, color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200' },
    aprobada: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
    rechazada: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
};

export default function VerificacionPublicaPage() {
    const { slug, id } = useParams();
    const [sol, setSol] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        solicitudesAPI.verificar(slug, id)
            .then(r => setSol(r.data))
            .catch(err => setError(err.response?.data?.detail || 'Solicitud no encontrada'))
            .finally(() => setLoading(false));
    }, [slug, id]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="max-w-md w-full text-center border-red-200">
                <CardContent className="py-8">
                    <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-bold mb-2">No encontrada</h2>
                    <p className="text-muted-foreground">{error}</p>
                </CardContent>
            </Card>
        </div>
    );

    const c = config[sol.estado] || config.creada;
    const Icon = c.icon;

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <Card className={`max-w-md w-full ${c.bg}`}>
                <CardContent className="py-8 text-center space-y-4">
                    <Shield className="h-8 w-8 text-primary mx-auto opacity-60" />
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Protección Industrial — Verificación</p>

                    <Icon className={`h-20 w-20 mx-auto ${c.color}`} />
                    <h2 className="text-lg font-bold">{sol.mensaje_estado}</h2>

                    <div className="text-sm text-left space-y-2 border-t pt-4">
                        <p><span className="text-muted-foreground">Tipo:</span> <strong>{sol.tipo_nombre}</strong></p>
                        <p><span className="text-muted-foreground">Solicitante:</span> <strong>{sol.solicitante?.nombre}</strong></p>
                        <p><span className="text-muted-foreground">Empresa:</span> <strong>{sol.solicitante?.empresa}</strong></p>
                        <p><span className="text-muted-foreground">Fecha:</span> <strong>{new Date(sol.created_at).toLocaleDateString('es-CL')}</strong></p>

                        {sol.estado === 'aprobada' && sol.resolucion && (
                            <div className="border-t pt-2 mt-2 space-y-1">
                                <p className="font-medium text-green-700">Autorización Vigente</p>
                                <p><span className="text-muted-foreground">Desde:</span> <strong>{new Date(sol.resolucion.vigencia_desde).toLocaleDateString('es-CL')}</strong></p>
                                {sol.resolucion.vigencia_hasta && (
                                    <p><span className="text-muted-foreground">Hasta:</span> <strong>{new Date(sol.resolucion.vigencia_hasta).toLocaleDateString('es-CL')}</strong></p>
                                )}
                                <p><span className="text-muted-foreground">Aprobado por:</span> <strong>{sol.resolucion.aprobado_por}</strong></p>
                                <a href={solicitudesAPI.getDocumentoPdfUrl(sol.id)} target="_blank" rel="noreferrer" className="block mt-2">
                                    <Button size="sm" className="w-full bg-green-600 hover:bg-green-700" data-testid="btn-descargar-pdf-publico">
                                        <Download className="h-4 w-4 mr-1" /> Descargar Documento
                                    </Button>
                                </a>
                            </div>
                        )}

                        {sol.estado === 'rechazada' && sol.resolucion && (
                            <div className="border-t pt-2 mt-2">
                                <p className="font-medium text-red-600">Motivo: {sol.resolucion.motivo}</p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
